import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("No session_id present");
      return;
    }
    const session_id = match[1];

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id });
        // Clear hash to avoid re-processing
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        const user = data.user;
        // Honor a stashed "return-to" URL set by deep links (e.g. anon clicks
        // on a driver profile from the public live map).
        let stashed = null;
        try { stashed = sessionStorage.getItem("ridegy:post_signin_redirect"); } catch { /* private mode */ }
        if (stashed) {
          try { sessionStorage.removeItem("ridegy:post_signin_redirect"); } catch { /* private mode */ }
          if (!user.role) {
            // Still need onboarding first; keep the stash so onboarding can redirect later
            try { sessionStorage.setItem("ridegy:post_signin_redirect", stashed); } catch { /* private mode */ }
            navigate("/onboarding", { replace: true });
          } else {
            navigate(stashed, { replace: true });
          }
          return;
        }
        if (!user.role) navigate("/onboarding", { replace: true });
        else navigate(`/${user.role}`, { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Authentication failed");
      }
    })();
  }, [location, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="border-2 border-foreground p-10 max-w-md w-full">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">
          RideGY · Auth
        </div>
        <div className="font-display text-3xl tracking-tighter mb-2" data-testid="auth-callback-title">
          {error ? "Sign-in failed" : "Signing you in…"}
        </div>
        <p className="text-foreground/70 text-sm" data-testid="auth-callback-message">
          {error || "Hold tight, we're verifying your Google account."}
        </p>
        {error && (
          <button
            onClick={() => (window.location.href = "/")}
            data-testid="auth-callback-back-btn"
            className="mt-6 px-4 py-2 border-2 border-foreground font-semibold"
          >
            Back to home
          </button>
        )}
      </div>
    </div>
  );
}
